import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './style/Payment.css';
import Loading from '../home/Loading';
import useUsersData from '../Auth/AllUsers';
import UsePaymentItems from '../Backend/UsePaymentItems';
import { API_CONFIG } from './../config'; 
import TopNavbar from '../home/TopNavbar';

const CREATE_USER_ENDPOINT = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ALLPAYMENTS}`;

function Pay() {
    const { item, userId } = useParams();
    const [isProcessing, setIsProcessing] = useState(false);
    const navigate = useNavigate();
    const { users } = useUsersData();
    const { paymentItems, loading } = UsePaymentItems();

    const safeDecode = (str) => {
        try {
            return str ? decodeURIComponent(atob(str)) : '';
        } catch (e) {
            return str || '';
        }
    };

    const phone = safeDecode(userId);
    const itemproduct = safeDecode(item) || '1'; 
    const coupon = "No";

    // Dynamic membership duration calculation
    const yearsCount = parseInt(itemproduct, 10) || 1;

    const matchUser = users?.find(user => user.phone === phone);
    const userName = matchUser?.name || "No";
    const email = matchUser?.email || "No";

    const matchItem = paymentItems?.find(item => String(item.id) === String(itemproduct));
    const amount = matchItem?.price || "299";
    const buyed = matchItem?.type || "No";
    const valid = matchItem?.valid || `${yearsCount} Year${yearsCount > 1 ? 's' : ''}`;

    const getSkuId = (id) => {
        return String(id);
    };

    const savePaymentDetails = async (purchaseToken) => {
        try {
            const payload = {
                name: userName,
                phone: phone,
                email: email,
                amount: amount,
                buyed: buyed,
                year: yearsCount,
                paymentId: purchaseToken,
                coupon: coupon,
            };

            const response = await fetch(CREATE_USER_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
                mode: 'cors'
            });

            const responseText = await response.text();
            console.log('🔵 Raw Backend Response:', responseText);

            let data;
            try {
                data = JSON.parse(responseText);
            } catch (parseError) {
                throw new Error('Server returned non-JSON response');
            }

            if (!response.ok) {
                throw new Error(data.message || `API failed with status ${response.status}`);
            }

            return data;
        } catch (error) {
            throw error;
        }
    };

    const handlePayment = async () => {
        if (!phone) {
            alert('Phone number is required');
            return;
        }

        if (!('getDigitalGoodsService' in window)) {
            alert('Google Play Billing is only available inside our official Android App.');
            return;
        }

        setIsProcessing(true);
        const skuId = getSkuId(itemproduct);

        try {
            // 1. Connect to Play Billing Service
            const service = await window.getDigitalGoodsService('https://play.google.com/billing');

            // 2. Safely inspect SKU details
            if (service && typeof service.getDetails === 'function') {
                try {
                    await service.getDetails([skuId]);
                } catch (e) {
                    console.warn('⚠️ getDetails skipped:', e);
                }
            }

            // 3. Clear existing unconsumed purchases
            if (service && typeof service.listPurchases === 'function') {
                try {
                    const existingPurchases = await service.listPurchases();
                    for (const p of existingPurchases) {
                        if (p.itemId === skuId && typeof service.consume === 'function') {
                            await service.consume(p.purchaseToken);
                        }
                    }
                } catch (e) {
                    console.warn('⚠️ listPurchases skipped:', e);
                }
            }

            // 4. Request Payment Sheet
            const paymentMethods = [{
                supportedMethods: 'https://play.google.com/billing',
                data: { sku: skuId }
            }];

            const paymentDetails = {
                total: {
                    label: `Subscription (${yearsCount} Year${yearsCount > 1 ? 's' : ''})`,
                    amount: { currency: 'INR', value: String(amount) }
                }
            };

            const request = new PaymentRequest(paymentMethods, paymentDetails);
            const paymentResponse = await request.show();

            const purchaseToken = paymentResponse.details.token;
            console.log('🟡 Payment successful in Play Store. Token:', purchaseToken);

            // 5. Save payment details in backend database FIRST
            try {
                await savePaymentDetails(purchaseToken);
                console.log('🟢 Payment details saved to DB successfully.');
            } catch (dbError) {
                console.error('🔴 Backend save failed:', dbError);
                try { await paymentResponse.complete('fail'); } catch (_) {}
                navigate(`/payment-success-fail/${purchaseToken}`);
                return;
            }

            // 6. Complete UI prompt
            try {
                await paymentResponse.complete('success');
            } catch (completeErr) {
                console.warn('⚠️ paymentResponse.complete failed or timed out:', completeErr);
            }

            // 7. Post-purchase cleanup (Acknowledge & Consume properly)
            if (service) {
                try {
                    if (typeof service.consume === 'function') {
                        // Consuming automatically acknowledges repeatable in-app purchases
                        await service.consume(purchaseToken);
                        console.log('🟢 Purchase consumed & acknowledged successfully via service.consume()');
                    } else if (typeof service.acknowledge === 'function') {
                        // Fallback acknowledge without invalid arguments
                        await service.acknowledge(purchaseToken);
                        console.log('🟢 Purchase acknowledged successfully via service.acknowledge()');
                    }
                } catch (consumeError) {
                    console.warn('⚠️ Acknowledge/Consume failed, attempting fallback acknowledge:', consumeError);
                    try {
                        if (typeof service.acknowledge === 'function') {
                            await service.acknowledge(purchaseToken);
                        }
                    } catch (e) {
                        console.error('🔴 Final acknowledge attempt failed:', e);
                    }
                }
            }

            // 8. REDIRECT TO SUCCESS PAGE
            setIsProcessing(false);
            navigate("/payment-success");

        } catch (error) {
            console.error('🔴 Google Play Billing Flow Error:', error);
            if (error.name !== 'AbortError') {
                alert(`Billing Error: ${error.message || 'Payment was canceled or failed.'}`);
            }
            setIsProcessing(false);
        }
    };

    if (loading) {
        return <Loading message='Loading payment page' />;
    }

    return (
        <section className='payment-container'>
            <TopNavbar name="Make payment" /><br /><br />
            <div className="payment-form-container">
                <h4>Complete Your Payment</h4>
                {userName && <p>{userName}</p>}
                <h4>{valid}</h4>
                <div className="fixed-amount-display">₹{amount}</div>

                <button
                    className="payment-submit-btn"
                    onClick={handlePayment}
                    disabled={isProcessing}
                >
                    {isProcessing ? 'Processing...' : `Pay ₹${amount}`}
                </button>

                <div style={{
                    marginTop: '24px',
                    padding: '16px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                    fontSize: '14px',
                    color: '#333',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                }}>
                    <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: '#6c757d', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Purchase Details:
                    </p>
                    <div><strong style={{ color: '#495057' }}>Name:</strong> {userName || 'Not found'}</div>
                    <div><strong style={{ color: '#495057' }}>Phone:</strong> {phone || 'Not found'}</div>
                    <div><strong style={{ color: '#495057' }}>Email:</strong> {email}</div>
                    <div><strong style={{ color: '#495057' }}>Validity:</strong> {yearsCount} Year{yearsCount > 1 ? 's' : ''}</div>
                    <div style={{ marginTop: '4px', paddingTop: '8px', borderTop: '1px solid #e9ecef', fontSize: '16px', fontWeight: 'bold', color: '#0d6efd' }}>
                        Amount: ₹{amount}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Pay;